import { useEffect, useState, type ReactElement } from 'react';
import Button from '../../../button';
import FindAReservationModal, {
	type FindAReservationAnalyticsEvent,
	type FindAReservationSubmitData,
} from '../../../find-a-reservation-modal';

export type FindReservationProps = {
	t: (text: string, params?: (string | number)[]) => string;
	property?: { name: string; owsCode: string };
	properties?: { name: string; owsCode: string }[];
	locale?: string;
	onSubmit: (values: FindAReservationSubmitData) => Promise<void>;
	onAnalyticsEvent?: (event: FindAReservationAnalyticsEvent) => void;
};

/** Ported from `FindReservation.tsx`: the "Have a stay booked? Add this to your itinerary." callout
 * from the Figma "Choose Your Dates" state, now opening the real `FindAReservationModal` (the legacy
 * `FindReservationModal` from shared-react-old). `resortCode`/`resortName` are passed through statically
 * since this widget always runs within a single property's context, so the modal's resort typeahead
 * isn't needed unless the host opts into it via `resortOptions`. */
export default function FindReservation({
	t,
	property,
	properties,
	locale = 'en',
	onSubmit,
	onAnalyticsEvent,
}: FindReservationProps): ReactElement {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<div
			className="flex flex-col items-center gap-150 bg-surface-recessed py-200 px-300 text-center"
			data-testid="find-reservation"
		>
			<span className="text-caption text-text-secondary">
				{t('Have a stay booked? Add this to your itinerary.')}
			</span>
			<Button intent="outline" className="w-full bg-white" onClick={() => setIsOpen(true)}>
				{t('Add to Itinerary')}
			</Button>

			<FindAReservationModal
				isOpen={isOpen}
				onDismiss={() => setIsOpen(false)}
				onSubmit={onSubmit}
				onSuccess={() => setIsOpen(false)}
				onAnalyticsEvent={onAnalyticsEvent}
				singleProperty={property ? { label: property.name, value: property.owsCode } : undefined}
				properties={properties?.map(({ name, owsCode }) => ({ label: name, value: owsCode }))}
			/>
		</div>
	);
}
